import { useLazyQuery, useMutation } from "@apollo/client";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CREATE_SERVICE_TICKET_HEADINGS } from "../../../../Constants/constants";
import {
  setCreateServiceTicketFail,
  setCreateServiceTicketSuccess,
  setOpenSelectReasons,
  setOpenServiceTicketDialog,
} from "../../../../Redux/Slices/TroubleTicketSlice";
import {
  addSpaceAfterComma,
  defaultFormatPhone,
  formatPhoneNumber,
  validInputSubmit,
} from "../../../../Services/helper";
import {
  CardHeading,
  CenterStack,
  CreateServiceticketStyledCard,
  ProfileTypography,
  StyledA,
  StyledDialog,
} from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";
import Button from "../../../../components/Button/Button";
import ContactSupport from "../../../../components/shared/ContactSupport/ContactSupport";
import { LoadingSpinner } from "../../../../components/shared/LoadingSpinner/LoadingSpinner";
import { CREATE_SERVICE_TICKET, GET_AVAILABLE_SLOTS_FOR_TT } from "../../query";
import SuccessDialogBox from "../NetworkStatus/SuccessDialogBox";
import { ContactInfoVerification } from "./ContactInfoVerification";
import RepairAppointmentDialog from "./RepairAppointmentDialog";
import useServiceTicketAPI from "./useServiceTicketAPI";

// Define proper type for the reason object
interface ReasonType {
  reason?: string;
  dispatchOrder?: boolean;
  isOther?: boolean;
}

function CreateServiceTicketNew({ getTroubleTicketStatus }: any) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectReason, setSelectReason] = useState<ReasonType>({});
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [shouldFetchSlots, setShouldFetchSlots] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [AvailableSlotsError, setavailableSlotsError] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>("");
  const [selectedDate, setSelectedDate] = useState<any>("");
  const { serviceAddress } = useSelector((state: any) => state.customerPlanDetails);
  const { telephoneContact, accountNumber, ndsObj, givenNameFamilyName } = useSelector(
    (state: any) => state.customerInfoDetails
  );
  const {
    openOutageBanner,
    existingTicket,
    createServiceTicketFail,
    createServiceTicketSuccess,
    openServiceTicketDialog,
    detailsForTTAvailableSlots,
    reasonsForTT,
    reasonsForTTError,
    getTTDetailsError,
    openSelectReasons,
  } = useSelector((state: any) => state.troubleTicketDetails);
  const customerDetails = useSelector((state: any) => state?.customerInfoDetails);
  const otherReason = selectReason?.reason?.toLowerCase() === "other";
  const formattedPhone = formatPhoneNumber(telephoneContact?.telephoneNumber);

  const initialFormData = {
    phoneNumber: formattedPhone || "",
    secondaryPhone: "",
    customerNotes: "",
  };

  const initialFormErrors = {
    phoneNumber: "",
    secondaryPhone: "",
    customerNotes: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const {
    getReasonCodesForTT,
    reasonCodesForTTLoading,
    getDetailsForAvailableSlots,
    getAvailableSlotsDetailsLoading,
  } = useServiceTicketAPI(accountNumber, customerDetails);

  useEffect(() => {
    if (openServiceTicketDialog) {
      dispatch(setOpenSelectReasons(true));
      setFormData(initialFormData);
      setFormErrors(initialFormErrors);
    } else {
      resetAllStates();
    }
  }, [openServiceTicketDialog, formattedPhone]);

  const [availableSlotsForTT, { loading: getAvailableSlotsLoading, data: availableSlots }] =
    useLazyQuery(GET_AVAILABLE_SLOTS_FOR_TT, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data && data?.availableSlotsForTT?.appointmentSlots.length > 0) {
          setShowAppointment(true);
          setavailableSlotsError(false);
        } else {
          setShowAppointment(true);
          setavailableSlotsError(true);
        }
      },
      onError: (error: any) => {
        setShowAppointment(true);
        setavailableSlotsError(true);
      },
    });

  const [createServiceTicket, { loading: createTicketLoading, data: serviceTicketData }] =
    useMutation(CREATE_SERVICE_TICKET, {
      onCompleted: (data) => {
        setIsSubmitting(false);
        setShowContactInfo(false);
        dispatch(setOpenSelectReasons(false));
        const error = data?.createServiceTicketByAccountNumber?.error;
        if (error) {
          dispatch(setCreateServiceTicketFail(true));
        } else {
          dispatch(setCreateServiceTicketSuccess(true));
        }
        getTroubleTicketStatus();
      },
      onError: () => {
        setIsSubmitting(false);
        dispatch(setCreateServiceTicketFail(true));
        dispatch(setOpenSelectReasons(false));
      },
    });

  const finalReason = selectReason?.isOther ? formData?.customerNotes : selectReason?.reason ?? "";
  const handleServiceTicket = async () => {
    try {
      setIsSubmitting(true);

      const serviceTicketInput = {
        customerName: givenNameFamilyName?.givenName,
        primaryNumber: defaultFormatPhone(formData.phoneNumber),
        customerNotes: formData.customerNotes,
        troubleTicketReason: selectReason?.reason,
        usi: ndsObj?.usi,
        addressId: detailsForTTAvailableSlots?.addressId,
        territoryId: detailsForTTAvailableSlots?.territoryId,
        accountUUID: detailsForTTAvailableSlots?.accountUuid,
        btn: detailsForTTAvailableSlots?.btn,
        ...(selectReason?.dispatchOrder && {
          firstOfferedDate:
            availableSlots?.availableSlotsForTT?.appointmentSlots[0]?.startDate ?? "",
          firstSelectedDate: selectedAppointment?.startDate ?? "",
          startDate: selectedAppointment?.startDate ?? "",
          endDate: selectedAppointment?.endDate,
          units: selectedAppointment?.workUnitsQuantity,
        }),
      };

      const response = await createServiceTicket({
        variables: {
          accountNumber,
          isNds: ndsObj?.isNDS,
          serviceTicketInput,
        },
      });

      if (response?.data) {
        setShowContactInfo(false);
        const { error } = response?.data?.createServiceTicketByAccountNumber || {};
        if (!error) {
          setCreateServiceTicketSuccess(true);
        } else {
          setCreateServiceTicketFail(true);
        }
      }
    } catch (err) {
      setIsSubmitting(false);
      dispatch(setCreateServiceTicketFail(true));
    }
  };

  useEffect(() => {
    if (shouldFetchSlots && selectReason.reason) {
      availableSlotsForTT({
        variables: {
          accountNumber,
          isNds: ndsObj?.isNDS,
          territoryId: detailsForTTAvailableSlots?.territoryId,
          reasonText: selectReason?.reason,
        },
      });

      setShouldFetchSlots(false);
    }
  }, [
    shouldFetchSlots,
    accountNumber,
    ndsObj?.isNDS,
    detailsForTTAvailableSlots?.territoryId,
    selectReason?.reason,
    availableSlotsForTT,
  ]);

  const resetAllStates = () => {
    setSelectReason({});
    setShowContactInfo(false);
    setShowAppointment(false);
    setShouldFetchSlots(false);
    setSelectedTimeSlot(null);
    setIsSubmitting(false);
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
  };

  const handleClose = () => {
    dispatch(setOpenServiceTicketDialog(false));
    resetAllStates();
  };

  const handleReasonNext = () => {
    if (selectReason.reason) {
      setShowContactInfo(true);
    }
  };

  const closeSuccessbox = () => {
    dispatch(setCreateServiceTicketSuccess(false));
    dispatch(setOpenServiceTicketDialog(false));
    navigate("/home");
  };

  const handleContactInfoNext = () => {
    const phoneNumberErrors = validInputSubmit({ phoneNumber: formData.phoneNumber });
    const customerNotesErrors =
      selectReason.reason === "Other"
        ? validInputSubmit({ customerNotes: formData.customerNotes })
        : {};

    const allErrors = {
      phoneNumber: phoneNumberErrors.phoneNumber || "",
      customerNotes: customerNotesErrors.customerNotes || "",
      secondaryPhone: "",
    };

    const isEmpty = Object.values(allErrors).every((value) => value === "");

    if (!isEmpty) {
      setFormErrors(allErrors);
      return;
    }

    if (selectReason?.dispatchOrder) {
      setShouldFetchSlots(true);
    } else {
      handleServiceTicket();
    }
  };

  const handleSelectAppointment = () => {
    if (selectedTimeSlot) {
      handleServiceTicket();
    }
  };

  const handleBackToReason = () => {
    setShowContactInfo(false);
    setFormErrors(initialFormErrors);
  };

  const handleBackToContactInfo = () => {
    setShowAppointment(false);
    setShowContactInfo(true);
  };

  const dialogHeading = () => {
    if (showAppointment) {
      return "We may need to schedule a repair appointment";
    } else if (showContactInfo) {
      return CREATE_SERVICE_TICKET_HEADINGS.secondary;
    } else {
      return CREATE_SERVICE_TICKET_HEADINGS.primary;
    }
  };

  const heading = dialogHeading();

  const handleBack = () => {
    if (showAppointment) {
      handleBackToContactInfo();
    } else if (showContactInfo) {
      handleBackToReason();
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (openOutageBanner || existingTicket || createServiceTicketFail) {
      dispatch(setOpenServiceTicketDialog(false));
    }
  }, [openOutageBanner, existingTicket, createServiceTicketFail, dispatch]);

  const handleRefresh = () => {
    if (AvailableSlotsError) {
      availableSlotsForTT({
        variables: {
          accountNumber,
          isNds: ndsObj?.isNDS,
          territoryId: detailsForTTAvailableSlots?.territoryId,
          reasonText: selectReason?.reason,
        },
      });
    }

    if (reasonsForTTError || getTTDetailsError) {
      getReasonCodesForTT();
      getDetailsForAvailableSlots();
    }
  };

  const ServiceTicketErrorMessage = ({ messageLine1, messageLine2 }: any) => {
    return (
      <CenterStack my={4} mt={8}>
        <Typography textAlign={"center"} fontSize={{ xs: "16px", md: "18px" }} fontWeight={600}>
          {messageLine1}
        </Typography>
        <Typography mt={3} fontSize={{ xs: "16px", md: "18px" }}>
          {messageLine2}
        </Typography>
        <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
          <Typography fontSize={"16px"}>If the issue persist,</Typography>
          <ContactSupport fontSize={"16px"} text="CHAT WITH AN AGENT" fontWeight={600} />
          <Typography fontSize={"16px"}>or</Typography>
          <Typography fontSize={"16px"}>Call</Typography>
          <StyledA sx={{ fontWeight: 600 }} href="tel:1-866-699-4759">
            1-866-MY-ZIPLY
          </StyledA>
          <Typography fontSize={"16px"}>for future assistance</Typography>
        </Stack>
        <Stack justifyContent="center" alignItems="center" mb={3} mt={6}>
          <Button type="payment" title="REFRESH" onClick={handleRefresh} />
        </Stack>
      </CenterStack>
    );
  };

  const renderDialogContent = () => {
    if (showAppointment && !createServiceTicketSuccess) {
      return (
        <>
          {AvailableSlotsError ? (
            <ServiceTicketErrorMessage
              messageLine1={"We are unable to retrieve available time slots at the moment."}
              messageLine2={
                "Kindly wait for a few moments as we attempt to retrieve the available time slots for you."
              }
            />
          ) : (
            <>
              <RepairAppointmentDialog
                open={showAppointment}
                availableSlotsForTT={availableSlots?.availableSlotsForTT}
                loading={getAvailableSlotsLoading}
                onBack={handleBackToContactInfo}
                onClose={handleClose}
                formData={formData}
                selectedReason={selectReason}
                setSelectedTimeSlot={setSelectedTimeSlot}
                selectedTimeSlot={selectedTimeSlot}
                setSelectedAppointment={setSelectedAppointment}
                setSelectedDate={setSelectedDate}
                selectedDate={selectedDate}
              />

              <Stack justifyContent="center" alignItems="center" mb={3}>
                <Button
                  type="payment"
                  title={"CREATE TICKET"}
                  onClick={handleSelectAppointment}
                  disabled={!selectedTimeSlot || isSubmitting}
                />
              </Stack>
            </>
          )}
        </>
      );
    } else if (showContactInfo) {
      return (
        <>
          <ContactInfoVerification
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            selectedReason={selectReason}
            otherReason={otherReason}
          />

          <Stack justifyContent="center" alignItems="center" mb={3}>
            <Button
              type="payment"
              title={selectReason.dispatchOrder ? "NEXT" : "CREATE TICKET"}
              onClick={handleContactInfoNext}
              disabled={
                Object.values(formErrors).some((error) => error !== "") ||
                shouldFetchSlots ||
                getAvailableSlotsLoading ||
                isSubmitting
              }
            />
          </Stack>
        </>
      );
    } else if (openSelectReasons) {
      return (
        <Grid container direction="row" justifyContent="space-around" width="100%" mb={2}>
          {reasonsForTTError || getTTDetailsError ? (
            <ServiceTicketErrorMessage
              messageLine1={" We are currently unable to retrieve the service ticket flow"}
              messageLine2={"  Please wait a moment and try again."}
            />
          ) : (
            <>
              {[...reasonsForTT]
                ?.sort((a, b) => (a?.reason === "Other" ? 1 : b?.reason === "Other" ? -1 : 0))
                ?.map((reasonObj: any) => (
                  <Grid item xs={12} md={5.5} key={reasonObj?.reason}>
                    <CreateServiceticketStyledCard
                      sx={{
                        margin: { xs: 2, md: "0.6rem 0" },
                        fontSize: { xs: "14px", md: "16px" },
                        border:
                          selectReason?.reason === reasonObj?.reason
                            ? `2px solid ${Theme.palette.secondary.main}`
                            : "",
                      }}
                      onClick={() => {
                        setSelectReason({
                          reason: reasonObj?.reason,
                          dispatchOrder: reasonObj?.dispatch,
                          isOther: reasonObj?.reason === "Other",
                        });
                      }}>
                      {reasonObj?.reason}
                    </CreateServiceticketStyledCard>
                  </Grid>
                ))}

              <Stack justifyContent="center" alignItems="center" width="100%" mt={3}>
                <Button
                  type="payment"
                  title="NEXT"
                  onClick={handleReasonNext}
                  disabled={!selectReason.reason}
                />
              </Stack>
            </>
          )}
        </Grid>
      );
    }
  };

  const handleClick = () => {
    closeSuccessbox();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <StyledDialog
        open={openServiceTicketDialog && openSelectReasons}
        maxWidth="md"
        fullWidth
        fullScreen={useMediaQuery(Theme.breakpoints.down("md"))}
        onClose={handleClose}
        aria-labelledby="service-ticket-dialog-title">
        <IconButton
          data-testid="close-button"
          sx={{ alignSelf: "flex-end", padding: 0 }}
          onClick={handleClose}
          aria-label="close">
          <CloseIcon />
        </IconButton>
        <Stack direction="row" alignItems="center">
          {!(reasonsForTTError || getTTDetailsError) && (
            <>
              <Box onClick={handleBack} sx={{ cursor: "pointer", pt: 1 }}>
                <ArrowBackIosIcon color="secondary" />
              </Box>
              <Stack
                direction={"row"}
                width={"100%"}
                justifyContent={"space-between"}
                alignContent={"center"}>
                <CardHeading
                  id="service-ticket-dialog-title"
                  sx={{ fontSize: { xs: "14px", md: "18px" } }}>
                  {heading}
                </CardHeading>
                <CardHeading
                  id="service-ticket-dialog-steps"
                  sx={{ fontSize: { xs: "14px", md: "18px" } }}>
                  Step {showAppointment || showContactInfo ? `2` : openSelectReasons && `1`} of 2
                </CardHeading>
              </Stack>
            </>
          )}
        </Stack>

        <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {renderDialogContent()}
        </DialogContent>
        {(getAvailableSlotsLoading ||
          createTicketLoading ||
          reasonCodesForTTLoading ||
          getAvailableSlotsDetailsLoading) && <LoadingSpinner />}
      </StyledDialog>

      <SuccessDialogBox
        open={createServiceTicketSuccess}
        onClose={closeSuccessbox}
        heading={CREATE_SERVICE_TICKET_HEADINGS.success}
        defaultDetails={[
          ...(serviceTicketData?.createServiceTicketByAccountNumber?.ticketNumber
            ? [
                {
                  label: "Ticket Number",
                  value: serviceTicketData?.createServiceTicketByAccountNumber?.ticketNumber,
                },
              ]
            : []),
          { label: "Primary contact number", value: formData.phoneNumber },
          ...(selectedDate && selectedTimeSlot
            ? [
                {
                  label: "Preferred appointment date and time",
                  value: `${moment(selectedDate).format("MMM D")}, ${selectedTimeSlot}`,
                },
              ]
            : []),
          { label: "Service address", value: addSpaceAfterComma(serviceAddress) },
          {
            label: "Reason",
            value: finalReason || "Not specified",
          },
        ]}
        note={
          <>
            <Stack my={2} spacing={1}>
              <ProfileTypography>Note : </ProfileTypography>
              <ProfileTypography>
                {CREATE_SERVICE_TICKET_HEADINGS.note}{" "}
                To check your ticket status <StyledA onClick={handleClick}>click here.</StyledA>
              </ProfileTypography>
            </Stack>
          </>
        }
      />
    </>
  );
}

export default CreateServiceTicketNew;
